import React, { useState, useEffect, useCallback } from 'react';
import {
  X, Scissors, Sparkles, Loader2, Plus, Trash2, Lock, Unlock,
  AlertTriangle, CheckCircle2, Film, Music, Save,
} from 'lucide-react';

/**
 * CBAToolsModal — Cut-By-Animatics editor.
 *
 * Workflow:
 *   1. PM clicks "CBA Tools" on the Animatic work card (only enabled once
 *      the animatic is client-approved).
 *   2. Modal opens — fetches existing clips OR offers "Auto-detect cuts"
 *      to seed a proposal.
 *   3. PM edits the timecode/label list.
 *   4. PM clicks "Save cuts" to persist.
 *   5. PM clicks "Lock & fan-out" to commit — system auto-creates Layout/
 *      Animation/Lighting/FX/Comp/Render shot tasks for each clip.
 *   6. Once locked, the per-clip shot tasks show up in the Layout tab,
 *      Animation tab, etc. — ready for assignment.
 */
export default function CBAToolsModal({ open, onClose, sequenceTask, api, onLocked }) {
  const [clips, setClips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [isLocked, setIsLocked] = useState(false);
  const [emergencyMeta, setEmergencyMeta] = useState(null);
  const [extractMsg, setExtractMsg] = useState('');
  const [extractStatus, setExtractStatus] = useState(null);

  const refresh = useCallback(async () => {
    if (!sequenceTask?.id || !api) return;
    setLoading(true); setErr('');
    try {
      const r = await api.get(`/cba/${sequenceTask.id}/clips`);
      setClips(r.data.clips || []);
      setIsLocked(!!r.data.is_locked);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to load clips');
    } finally {
      setLoading(false);
    }
  }, [sequenceTask?.id, api]);

  const refreshExtractStatus = useCallback(async () => {
    if (!sequenceTask?.id || !api) return;
    try {
      const r = await api.get(`/cba/${sequenceTask.id}/extract-status`);
      setExtractStatus(r.data || null);
    } catch (e) { /* ignore */ }
  }, [sequenceTask?.id, api]);

  // Read the seq task itself so we can show the Emergency banner if the
  // admin already ran the break-glass flow on this project (Feb 2026).
  useEffect(() => {
    if (!open || !sequenceTask?.project_id || !api) return;
    api.get(`/cba/admin/emergency/status/${sequenceTask.project_id}`)
      .then(r => setEmergencyMeta(r.data || null))
      .catch(() => setEmergencyMeta(null));
  }, [open, sequenceTask?.project_id, api]);

  useEffect(() => { if (open) refresh(); }, [open, refresh]);
  useEffect(() => { if (open) refreshExtractStatus(); }, [open, refreshExtractStatus]);

  if (!open) return null;

  const autoDetect = async (pmOverride = false) => {
    setBusy(true); setErr(''); setMsg('');
    try {
      const r = await api.post(`/cba/${sequenceTask.id}/auto-detect`, pmOverride ? { pm_override: true } : {});
      const proposed = r.data.proposed_clips || [];
      // Merge into local state — user can edit before saving
      setClips(proposed);
      setMsg(`Proposed ${proposed.length} cuts (${r.data.source})${pmOverride ? ' · PM override' : ''}. Edit timecodes and click Save.`);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Auto-detect failed';
      if (e.response?.status === 409 && !pmOverride) {
        // Offer PM override path — founder use case: verbal client approval
        // landed but formal review click hasn't come through yet.
        if (window.confirm(
          `${msg}\n\nDo you have verbal client go-ahead? Click OK to override the gate and start cutting now (audit-logged).`
        )) {
          return autoDetect(true);
        }
        setErr('CBA generation is gated until the animatic is client-approved.');
      } else {
        setErr(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  const saveClips = async () => {
    setBusy(true); setErr(''); setMsg('');
    try {
      const cleaned = clips.map((c, i) => ({
        clip_index: i + 1,
        label: c.label || `SH_${(i + 1).toString().padStart(3, '0')}`,
        start_tc: c.start_tc || '',
        end_tc: c.end_tc || '',
        duration_sec: parseFloat(c.duration_sec) || 0,
        notes: c.notes || '',
      }));
      const r = await api.post(`/cba/${sequenceTask.id}/clips`, { clips: cleaned });
      setMsg(`Saved ${r.data.count} cuts.`);
      await refresh();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const lockAndFanout = async () => {
    if (!window.confirm(
      `Lock CBA and create downstream shot tasks?\n\n` +
      `This will create Layout → Animation → Lighting → FX → Comp → Render tasks for each of ${clips.length} clip(s).\n\n` +
      `You can unlock later, but already-created shot tasks won't be deleted.`
    )) return;
    setBusy(true); setErr(''); setMsg('');
    try {
      const r = await api.post(`/cba/${sequenceTask.id}/lock`);
      setMsg(r.data.message || 'CBA locked.');
      setIsLocked(true);
      if (onLocked) onLocked(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Lock failed');
    } finally {
      setBusy(false);
    }
  };

  const unlock = async () => {
    if (!window.confirm('Unlock CBA so you can edit cuts? Existing shot tasks remain — you must delete them manually if the cut structure changes radically.')) return;
    setBusy(true);
    try {
      await api.post(`/cba/${sequenceTask.id}/unlock`);
      setIsLocked(false);
      setMsg('CBA unlocked.');
    } catch (e) {
      setErr(e.response?.data?.detail || 'Unlock failed');
    } finally {
      setBusy(false);
    }
  };

  // Extract clips → NAS. Dispatches one ffmpeg_chop job per clip via the
  // QNAP bridge. Each job creates a SH_NNN/ folder containing video + WAV.
  // Layout artists can then open the folder directly. (Founder spec May 2026.)
  const extractClips = async () => {
    if (!window.confirm(`Extract ${clips.length} shot(s) to NAS? Each shot will be written as SH_NNN/{video, wav} under the project's CBA folder. Bridge must be online.`)) return;
    setBusy(true); setErr(''); setExtractMsg('');
    try {
      const r = await api.post(`/cba/${sequenceTask.id}/extract-clips`, {});
      setExtractMsg(r.data?.message || `Dispatched ${r.data?.dispatched ?? 0} jobs.`);
      await refreshExtractStatus();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Extract failed');
    } finally {
      setBusy(false);
    }
  };

  const addClip = () => {
    setClips(prev => [...prev, {
      id: `_new_${Date.now()}`,
      clip_index: prev.length + 1,
      label: `SH_${(prev.length + 1).toString().padStart(3, '0')}`,
      start_tc: '', end_tc: '', duration_sec: 0,
    }]);
  };

  const removeClip = (i) => {
    setClips(prev => prev.filter((_, idx) => idx !== i));
  };

  const updateClip = (i, key, value) => {
    setClips(prev => prev.map((c, idx) => idx === i ? { ...c, [key]: value } : c));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-zinc-950 border border-violet-500/40 rounded-md w-[860px] max-w-[95vw] max-h-[90vh] overflow-hidden flex flex-col"
           onClick={e => e.stopPropagation()}
           data-testid="cba-tools-modal">
        {/* Header */}
        <div className="px-5 py-4 border-b border-zinc-800 flex items-center gap-3 bg-gradient-to-r from-violet-900/30 via-fuchsia-900/20 to-transparent">
          <div className="w-10 h-10 rounded bg-violet-500/20 text-violet-300 flex items-center justify-center">
            <Scissors className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-heading font-semibold text-white">Cut-By-Animatics · CBA Editor</h2>
            <p className="text-[11px] text-zinc-400 truncate">{sequenceTask?.title}</p>
          </div>
          {isLocked && (
            <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 flex items-center gap-1">
              <Lock className="w-3 h-3" /> LOCKED
            </span>
          )}
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="cba-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="px-5 py-3 border-b border-zinc-800 flex items-center gap-2 bg-zinc-900/50">
          {!isLocked && (
            <button onClick={autoDetect} disabled={busy}
              data-testid="cba-auto-detect"
              className="px-3 py-1.5 rounded bg-fuchsia-500/15 hover:bg-fuchsia-500/25 text-fuchsia-300 border border-fuchsia-500/30 text-[11px] font-medium flex items-center gap-1.5 disabled:opacity-40">
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
              Auto-detect cuts
            </button>
          )}
          {!isLocked && (
            <button onClick={addClip} disabled={busy}
              data-testid="cba-add-clip"
              className="px-3 py-1.5 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-[11px] flex items-center gap-1.5">
              <Plus className="w-3 h-3" /> Add clip
            </button>
          )}
          <span className="text-[10px] text-zinc-500 ml-auto">
            {clips.length} clip{clips.length === 1 ? '' : 's'} · Each clip will spawn 6 shot tasks (Layout → Render)
          </span>
          {!isLocked ? (
            <>
              <button onClick={saveClips} disabled={busy || !clips.length}
                data-testid="cba-save-clips"
                className="px-3 py-1.5 rounded bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 border border-emerald-500/40 text-[11px] flex items-center gap-1.5 disabled:opacity-40">
                <Save className="w-3 h-3" /> Save cuts
              </button>
              <button onClick={lockAndFanout} disabled={busy || !clips.length}
                data-testid="cba-lock"
                className="px-3 py-1.5 rounded bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-400 hover:to-fuchsia-400 text-white text-[11px] font-medium flex items-center gap-1.5 disabled:opacity-40 shadow-lg shadow-violet-500/20">
                <Lock className="w-3 h-3" /> Lock & fan-out
              </button>
            </>
          ) : (
            <>
              <button onClick={extractClips} disabled={busy || !clips.length}
                data-testid="cba-extract-clips-btn"
                title="Run FFmpeg on the QNAP bridge — chops the master animatic into per-shot folders (SH_001/, SH_002/...) each containing the video segment + WAV audio."
                className="px-3 py-1.5 rounded bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-200 border border-cyan-500/40 text-[11px] flex items-center gap-1.5 disabled:opacity-40">
                <Scissors className="w-3 h-3" /> Extract → NAS
              </button>
              <button onClick={unlock} disabled={busy}
                data-testid="cba-unlock"
                className="px-3 py-1.5 rounded bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 border border-amber-500/30 text-[11px] flex items-center gap-1.5 disabled:opacity-40">
                <Unlock className="w-3 h-3" /> Unlock
              </button>
            </>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {emergencyMeta?.cba_emergency_used && (
            <div className="mb-3 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded text-[11px] text-emerald-200 flex items-start gap-2"
                 data-testid="cba-emergency-banner">
              <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-emerald-100">CBA already executed via Emergency override.</p>
                <p className="text-[10px] text-emerald-300/80 mt-1">
                  Ran on {(emergencyMeta.cba_emergency_at || '').slice(0, 16)} by {emergencyMeta.cba_emergency_by_email || 'admin'} —
                  {emergencyMeta.clip_count} clips, {emergencyMeta.shot_task_count} Layout shot tasks.
                  {emergencyMeta.client_approved
                    ? ` Client formally approved on ${(emergencyMeta.client_approved_at || '').slice(0, 16)} — normal flow short-circuited.`
                    : ' Client approval still pending (verbal only).'}
                </p>
              </div>
            </div>
          )}
          {err && (
            <div className="mb-3 p-2.5 bg-rose-500/10 border border-rose-500/30 rounded text-[11px] text-rose-300 flex items-start gap-2">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5" /> {err}
            </div>
          )}
          {msg && (
            <div className="mb-3 p-2.5 bg-emerald-500/10 border border-emerald-500/30 rounded text-[11px] text-emerald-300 flex items-start gap-2">
              <CheckCircle2 className="w-3.5 h-3.5 mt-0.5" /> {msg}
            </div>
          )}
          {extractMsg && (
            <div className="mb-3 p-2.5 bg-cyan-500/10 border border-cyan-500/30 rounded text-[11px] text-cyan-300 flex items-start gap-2"
                 data-testid="cba-extract-msg">
              <Scissors className="w-3.5 h-3.5 mt-0.5" /> {extractMsg}
            </div>
          )}
          {extractStatus?.summary && (extractStatus.summary.ok + extractStatus.summary.error + extractStatus.summary.dispatched > 0) && (
            <div className="mb-3 p-2.5 bg-zinc-900 border border-zinc-800 rounded" data-testid="cba-extract-status">
              <p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1">NAS extraction status</p>
              <div className="flex gap-3 text-[11px]">
                <span className="text-emerald-300">✓ {extractStatus.summary.ok} extracted</span>
                <span className="text-amber-300">… {extractStatus.summary.dispatched} dispatched</span>
                {extractStatus.summary.error > 0 && (
                  <span className="text-rose-300">✗ {extractStatus.summary.error} failed</span>
                )}
              </div>
            </div>
          )}
          {loading ? (
            <p className="text-zinc-500 text-xs text-center py-12">Loading clips…</p>
          ) : clips.length === 0 ? (
            <div className="text-center py-16">
              <Scissors className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
              <p className="text-sm text-zinc-300 font-medium mb-1">No cuts defined yet</p>
              <p className="text-[11px] text-zinc-500 mb-4">
                Once the client approves the animatic, run auto-detect or add cuts manually below.
              </p>
              <button onClick={autoDetect} disabled={busy}
                className="px-4 py-2 rounded bg-fuchsia-500 hover:bg-fuchsia-400 text-white text-xs font-medium inline-flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5" /> Auto-detect cuts
              </button>
            </div>
          ) : (
            <table className="w-full text-[11px]" data-testid="cba-clips-table">
              <thead>
                <tr className="text-[9px] uppercase text-zinc-500 border-b border-zinc-800">
                  <th className="text-left px-2 py-2 w-12">#</th>
                  <th className="text-left px-2 py-2 w-32">Label</th>
                  <th className="text-left px-2 py-2 w-28">Start TC</th>
                  <th className="text-left px-2 py-2 w-28">End TC</th>
                  <th className="text-left px-2 py-2 w-20">Duration</th>
                  <th className="text-left px-2 py-2">Shot Tasks</th>
                  {!isLocked && <th className="px-2 py-2 w-10"></th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800/50">
                {clips.map((c, i) => (
                  <tr key={c.id || i} className="text-zinc-300" data-testid={`cba-clip-row-${i}`}>
                    <td className="px-2 py-2 text-zinc-600 font-mono">{i + 1}</td>
                    <td className="px-2 py-2">
                      <input type="text" value={c.label || ''}
                        disabled={isLocked || busy}
                        onChange={e => updateClip(i, 'label', e.target.value)}
                        className="w-full bg-zinc-900 border border-zinc-800 rounded px-2 py-1 text-[11px] disabled:opacity-60 font-mono" />
                    </td>
                    <td className="px-2 py-2">
                      <input type="text" value={c.start_tc || ''}
                        disabled={isLocked || busy}
                        onChange={e => updateClip(i, 'start_tc', e.target.value)}
                        placeholder="00:00:00.000"
                        className="w-full bg-zinc-900 border border-zinc-800 rounded px-2 py-1 text-[11px] disabled:opacity-60 font-mono" />
                    </td>
                    <td className="px-2 py-2">
                      <input type="text" value={c.end_tc || ''}
                        disabled={isLocked || busy}
                        onChange={e => updateClip(i, 'end_tc', e.target.value)}
                        placeholder="00:00:06.000"
                        className="w-full bg-zinc-900 border border-zinc-800 rounded px-2 py-1 text-[11px] disabled:opacity-60 font-mono" />
                    </td>
                    <td className="px-2 py-2">
                      <input type="number" step="0.1" value={c.duration_sec || 0}
                        disabled={isLocked || busy}
                        onChange={e => updateClip(i, 'duration_sec', e.target.value)}
                        className="w-full bg-zinc-900 border border-zinc-800 rounded px-2 py-1 text-[11px] disabled:opacity-60" />
                    </td>
                    <td className="px-2 py-2 text-[10px] text-zinc-500">
                      {(c.shot_tasks || []).length > 0 ? (
                        <span className="text-emerald-400">{(c.shot_tasks || []).length} tasks created</span>
                      ) : (
                        <span className="text-zinc-600">— (lock to fan out)</span>
                      )}
                    </td>
                    {!isLocked && (
                      <td className="px-2 py-2 text-right">
                        <button onClick={() => removeClip(i)} disabled={busy}
                          className="text-rose-500 hover:text-rose-300 disabled:opacity-40">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-zinc-800 bg-zinc-900/50 flex items-center gap-2">
          <Film className="w-3.5 h-3.5 text-zinc-500" />
          <p className="text-[10px] text-zinc-500 flex-1">
            CBA = Cut-By-Animatics. Each clip becomes a shot card in Layout / Animation / Lighting / FX / Comp / Render.
            <span className="hidden md:inline"> Lock when cuts are final — artists can then start work.</span>
          </p>
          <button onClick={onClose} className="text-[11px] text-zinc-400 hover:text-white px-3 py-1">Close</button>
        </div>
      </div>
    </div>
  );
}
